import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Users, DollarSign, ChevronDown, CheckCircle2, ArrowRight,
  Loader2, Send, Briefcase, Building2, GraduationCap
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const REFERRAL_TARGETS = [
  'Training Center',
  'Recruitment Agency',
  'University / College',
  'Corporate HR Department',
  'Other'
];

export default function PartnersPage() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    full_name: '',
    company_role: '',
    email: '',
    whatsapp: '',
    referral_target: '',
    referred_company_name: '',
    message: ''
  });

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) { toast.error('Please enter your full name'); return; }
    if (!form.email.trim()) { toast.error('Please enter your email'); return; }
    if (!form.whatsapp.trim()) { toast.error('Please enter your WhatsApp number'); return; }
    if (!form.referral_target) { toast.error('Please select who you will refer'); return; }
    if (!form.referred_company_name.trim()) { toast.error('Please enter the referred company name'); return; }

    setSubmitting(true);
    try {
      await axios.post(`${API}/partners/submit`, form);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero */}
      <section className="bg-gray-900 text-white py-20">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <span className="inline-block bg-[#CE1126] text-white text-xs font-semibold px-3 py-1 rounded-full mb-5 uppercase tracking-wide">
            Partner Programme
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="partners-hero-title">
            Earn Recurring Income.<br />Refer Training Centers &amp; Agencies.
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto mb-8">
            Know a training centre, recruitment agency, or university that needs bulk CV &amp; interview tools?
            Refer them to Daily UAE Jobs and earn commission on every deal.
          </p>
          <a href="#partner-form">
            <Button className="btn-uae-red px-8 py-3 text-base" data-testid="become-partner-btn">
              Become a Partner <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-2xl font-bold text-center mb-12">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '1', title: 'Sign Up', desc: 'Fill in the partner form below with your details and who you want to refer.', icon: Users },
              { step: '2', title: 'We Close the Deal', desc: 'Our B2B team contacts the referral, demos the product, and handles onboarding.', icon: Briefcase },
              { step: '3', title: 'You Earn Commission', desc: 'Once the deal is live, you receive a recurring commission every month.', icon: DollarSign }
            ].map(s => (
              <div key={s.step} className="text-center">
                <div className="w-14 h-14 rounded-full bg-[#CE1126] bg-opacity-10 flex items-center justify-center mx-auto mb-4">
                  <s.icon className="w-7 h-7 text-[#CE1126]" />
                </div>
                <div className="text-xs font-bold text-[#CE1126] mb-1">STEP {s.step}</div>
                <h3 className="text-lg font-bold mb-2">{s.title}</h3>
                <p className="text-sm text-gray-600">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Earnings Example */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl font-bold text-center mb-8">Earnings Example</h2>
          <Card className="p-8 bg-gray-900 text-white">
            <div className="grid sm:grid-cols-3 gap-6 text-center">
              <div>
                <p className="text-3xl font-bold text-[#CE1126]">AED 500</p>
                <p className="text-sm text-gray-400 mt-1">Per referral / month</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[#009639]">3 referrals</p>
                <p className="text-sm text-gray-400 mt-1">Active accounts</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-yellow-400">AED 1,500</p>
                <p className="text-sm text-gray-400 mt-1">Monthly income</p>
              </div>
            </div>
            <p className="text-center text-gray-400 text-xs mt-6">
              Commission rates vary by deal size. Amounts shown are illustrative.
            </p>
          </Card>
        </div>
      </section>

      {/* Who Can Join */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl font-bold text-center mb-8">Who Can Join?</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { title: 'Career Coaches', icon: Users },
              { title: 'HR Consultants', icon: Briefcase },
              { title: 'Training Centres', icon: GraduationCap },
              { title: 'Anyone with Contacts', icon: Building2 }
            ].map(w => (
              <Card key={w.title} className="p-5 text-center">
                <w.icon className="w-8 h-8 text-[#009639] mx-auto mb-3" />
                <p className="font-semibold text-sm">{w.title}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Form */}
      <section id="partner-form" className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-2xl">
          <h2 className="text-2xl font-bold text-center mb-2">Become a Referral Partner</h2>
          <p className="text-gray-600 text-center text-sm mb-8">
            Fill in the form below. Our team will reach out within 24 hours.
          </p>

          {submitted ? (
            <Card className="p-10 text-center" data-testid="partner-success">
              <CheckCircle2 className="w-16 h-16 text-[#009639] mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2 text-[#009639]">Application Received!</h3>
              <p className="text-gray-600">Our team will contact you within 24 hours.</p>
            </Card>
          ) : (
            <Card className="p-6" data-testid="partner-form">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                    <input
                      type="text" value={form.full_name} onChange={e => set('full_name', e.target.value)}
                      placeholder="Your full name"
                      className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE1126]"
                      data-testid="partner-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company / Role</label>
                    <input
                      type="text" value={form.company_role} onChange={e => set('company_role', e.target.value)}
                      placeholder="e.g. Career Coach, HR Manager"
                      className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE1126]"
                      data-testid="partner-role-input"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input
                      type="email" value={form.email} onChange={e => set('email', e.target.value)}
                      placeholder="your@email.com"
                      className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE1126]"
                      data-testid="partner-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp *</label>
                    <input
                      type="text" value={form.whatsapp} onChange={e => set('whatsapp', e.target.value)}
                      placeholder="+971 50 XXX XXXX"
                      className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE1126]"
                      data-testid="partner-whatsapp-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Who will you refer? *</label>
                  <div className="relative">
                    <select
                      value={form.referral_target} onChange={e => set('referral_target', e.target.value)}
                      className="w-full h-10 rounded-md border border-gray-300 bg-white px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE1126] appearance-none"
                      data-testid="partner-target-select"
                    >
                      <option value="">Select type</option>
                      {REFERRAL_TARGETS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-3 pointer-events-none" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Referred Company Name *</label>
                  <input
                    type="text" value={form.referred_company_name} onChange={e => set('referred_company_name', e.target.value)}
                    placeholder="Name of the company you're referring"
                    className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE1126]"
                    data-testid="partner-company-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message <span className="text-gray-400">(optional)</span></label>
                  <textarea
                    value={form.message} onChange={e => set('message', e.target.value)}
                    rows={3} placeholder="Anything else you'd like us to know..."
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE1126]"
                    data-testid="partner-message-input"
                  />
                </div>

                <Button
                  type="submit" className="w-full btn-uae-red py-3"
                  disabled={submitting}
                  data-testid="partner-submit-btn"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" /> Submit Partner Application</>
                  )}
                </Button>
              </form>
            </Card>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
